import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GraduationCap, User, SignOut, ChartLine, BookOpen, Crown } from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const linkCls = ({ isActive }) =>
        `text-sm tracking-wide transition-colors duration-150 hover:text-ink ${
            isActive ? "text-ink font-semibold" : "text-ink/60"
        }`;

    return (
        <header className="sticky top-0 z-40 glass border-b border-ink/10" data-testid="navbar">
            <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                    <span className="w-9 h-9 grid place-items-center bg-ink text-bone rounded-sm">
                        <GraduationCap size={20} weight="duotone" />
                    </span>
                    <div>
                        <div className="font-serif text-lg leading-none text-ink">Wibpro</div>
                        <div className="text-[10px] tracking-[0.28em] text-ink/60 uppercase">Academy</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <NavLink to="/courses" className={linkCls} data-testid="nav-courses">Courses</NavLink>
                    <NavLink to="/tuitions" className={linkCls} data-testid="nav-tuitions">Tuitions</NavLink>
                    <NavLink to="/resources" className={linkCls} data-testid="nav-resources">Papers</NavLink>
                    <NavLink to="/study-abroad" className={linkCls} data-testid="nav-study-abroad">Study Abroad</NavLink>
                    {user && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink>}
                    {user && <NavLink to="/results" className={linkCls} data-testid="nav-results">Results</NavLink>}
                    <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</NavLink>
                </nav>

                <div className="flex items-center gap-3">
                    {!user ? (
                        <>
                            <Button variant="ghost" size="sm" onClick={() => nav("/login")} data-testid="btn-nav-login" className="text-ink hover:bg-ink/5">Sign in</Button>
                            <Button size="sm" onClick={() => nav("/register")} data-testid="btn-nav-register" className="bg-ink text-bone hover:bg-ink/90 rounded-sm">Get started</Button>
                        </>
                    ) : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="btn-user-menu" className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-ink/15 hover:bg-ink/5 transition-colors">
                                    <div className="w-7 h-7 rounded-full bg-ink text-bone grid place-items-center text-xs font-semibold">
                                        {user.name?.[0]?.toUpperCase() || "S"}
                                    </div>
                                    <span className="hidden sm:inline text-sm text-ink">{user.name}</span>
                                    {user.is_premium && <Crown size={14} weight="fill" className="text-gold" />}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[200px]">
                                <DropdownMenuLabel className="font-serif">{user.name}<div className="text-xs font-sans text-muted-foreground font-normal">{user.email}</div></DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard"><ChartLine size={16} className="mr-2"/> Dashboard</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => nav("/courses")} data-testid="menu-courses"><BookOpen size={16} className="mr-2"/> Courses</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => nav("/results")} data-testid="menu-results"><User size={16} className="mr-2"/> My results</DropdownMenuItem>
                                {user.is_admin && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin" className="text-gold-dark"><Crown size={16} className="mr-2" weight="fill"/> Admin console</DropdownMenuItem>}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="text-destructive"><SignOut size={16} className="mr-2"/> Log out</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </header>
    );
}
